import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CMSTeam = () => {
  const { getAuthHeader } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', position: '', image: '', bio: '' });

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/team`, { headers: getAuthHeader() });
      setTeam(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/cms/team/${editing.id}`, formData, { headers: getAuthHeader() });
      } else {
        await axios.post(`${BACKEND_URL}/api/cms/team`, formData, { headers: getAuthHeader() });
      }
      setDialogOpen(false);
      setEditing(null);
      setFormData({ name: '', position: '', image: '', bio: '' });
      fetchTeam();
    } catch (error) {
      alert('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this team member?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/cms/team/${id}`, { headers: getAuthHeader() });
      fetchTeam();
    } catch (error) {
      alert('Failed to delete');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between">
        <h2 className="text-2xl font-bold">Team Management</h2>
        <Button onClick={() => { setEditing(null); setFormData({ name: '', position: '', image: '', bio: '' }); setDialogOpen(true); }} className="bg-primary">
          <Plus size={20} className="mr-2" /> Add Member
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {team.map((member) => (
          <Card key={member.id}>
            <div className="h-48"><img src={member.image} alt={member.name} className="w-full h-full object-cover" /></div>
            <CardHeader><CardTitle>{member.name}</CardTitle><p className="text-sm text-gray-600">{member.position}</p></CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-3">{member.bio.substring(0, 80)}...</p>
              <div className="flex gap-2">
                <Button onClick={() => { setEditing(member); setFormData(member); setDialogOpen(true); }} size="sm" variant="outline"><Edit size={16} /></Button>
                <Button onClick={() => handleDelete(member.id)} size="sm" variant="outline" className="text-red-600"><Trash2 size={16} /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Team Member</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="block text-sm font-medium mb-2">Name *</label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium mb-2">Position *</label><Input value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium mb-2">Image URL *</label><Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium mb-2">Bio *</label><Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} required rows={3} /></div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">{editing ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSTeam;
